'use client';

import { ORDER_STATUS } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { Requester } from '@/lib/Requester';
import { formatNumberToPriceText, formatOrderDate } from '@/lib/utils';
import { GenericResponse, StatusResponse } from '@/models/GenericResponse';
import { Order } from '@/models/Order';
import {
  ChevronRight,
  CircleArrowLeft,
  CircleX,
  HandCoins,
  Hourglass,
  MessageCircleWarning,
  MessageCircleX,
  Package,
  PackageCheck,
  PackageOpen,
  PenLine,
  SquareX,
  Truck,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import SmartImage from './SmartImage';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Ratings } from './ui/stars';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { useToast } from './ui/use-toast';
import { WebLink } from '@/lib/WebLinks';

enum ORDER_TAB {
  TOTAL_ORDER = 'total',
  ACTIVE_ORDER = 'active',
  COMPLETED_ORDER = 'completed',
  CANCELED = 'canceled',
}

interface OrderSection {
  count: number;
  title: string;
  orders: Order[];
}

export default function OrderHistory({
  orders,
}: {
  orders: Order[];
}) {
  const router = useRouter();
  const [ordersList, setOrdersList] = useState<Record<ORDER_TAB, OrderSection>>(
    {} as Record<ORDER_TAB, OrderSection>,
  );
  const [selectedTab, setSelectedTab] = useState<ORDER_TAB>(ORDER_TAB.ACTIVE_ORDER);

  const openOrderDetail = (order: Order) => {
    router.push(WebLink.ORDER_DETAIL(order.orderNumber, order.orderId));
  };

  useEffect(() => {
    const activeOrders: Order[] = [];
    const completedOrders: Order[] = [];
    const canceledOrders: Order[] = [];

    orders.forEach((order) => {
      if (
        [
          ORDER_STATUS.PRINTED,
          ORDER_STATUS.PREPARING,
          ORDER_STATUS.PAYMENT_PENDING,
          ORDER_STATUS.PAYMENT_DONE,
          ORDER_STATUS.ON_THE_COUNTER,
          ORDER_STATUS.ON_DELIVERY,
          ORDER_STATUS.ASSIGNED_TO_COURIER,
          ORDER_STATUS.SHIPPED,
        ].includes(order.orderStatus)
      ) {
        activeOrders.push(order);
        return;
      }
      if ([ORDER_STATUS.DELIVERED].includes(order.orderStatus)) {
        completedOrders.push(order);
        return;
      }

      canceledOrders.push(order);
    });

    const record = {
      [ORDER_TAB.TOTAL_ORDER]: {
        count: orders.length,
        title: 'Tümü',
        orders: orders,
      } as OrderSection,
      [ORDER_TAB.ACTIVE_ORDER]: {
        count: activeOrders.length,
        title: 'Aktif',
        orders: activeOrders,
      } as OrderSection,
      [ORDER_TAB.COMPLETED_ORDER]: {
        count: completedOrders.length,
        title: 'Tamamlanan',
        orders: completedOrders,
      } as OrderSection,
      [ORDER_TAB.CANCELED]: {
        count: canceledOrders.length,
        title: 'İptal Edilen',
        orders: canceledOrders,
      } as OrderSection,
    } as Record<ORDER_TAB, OrderSection>;

    setOrdersList(record);
  }, [orders]);

  return (
    <Tabs defaultValue={ORDER_TAB.ACTIVE_ORDER} value={selectedTab}>
      <TabsList className="grid sm:grid-cols-4 grid-cols-2 bg-transparent h-fit gap-4">
        <TabsTrigger
          value={ORDER_TAB.TOTAL_ORDER}
          className={`flex flex-col items-center border rounded-xl hover:scale-105 transition-all duration-300
            ${selectedTab !== ORDER_TAB.TOTAL_ORDER ? 'borde-hzc_beige_400' : 'border-hzc_purple_500'}`}
          onClick={() => setSelectedTab(ORDER_TAB.TOTAL_ORDER)}
        >
          <div className="flex flex-row gap-2 justify-start items-center w-full">
            <div className="flex items-center justify-center size-9 rounded-full bg-purple-200">
              <Package className="text-purple-500 w-4 h-4" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-base font-medium text-hzc_beige_900">
                {ordersList[ORDER_TAB.TOTAL_ORDER]?.count}
              </span>
              <span className="text-xs text-hzc_beige_700">
                {ordersList[ORDER_TAB.TOTAL_ORDER]?.title}
              </span>
            </div>
          </div>
        </TabsTrigger>
        <TabsTrigger
          value={ORDER_TAB.ACTIVE_ORDER}
          className={`flex flex-col items-center border rounded-xl hover:scale-105 transition-all duration-300
            ${selectedTab !== ORDER_TAB.ACTIVE_ORDER ? 'borde-hzc_beige_400' : 'border-hzc_purple_500'}`}
          onClick={() => setSelectedTab(ORDER_TAB.ACTIVE_ORDER)}
        >
          <div className="flex flex-row gap-2 justify-start items-center w-full">
            <div className="flex items-center justify-center size-9 rounded-full bg-blue-200">
              <Package className="text-blue-500 w-4 h-4" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-base font-medium text-hzc_beige_900">
                {ordersList[ORDER_TAB.ACTIVE_ORDER]?.count}
              </span>
              <span className="text-xs text-hzc_beige_700">
                {ordersList[ORDER_TAB.ACTIVE_ORDER]?.title}
              </span>
            </div>
          </div>
        </TabsTrigger>
        <TabsTrigger
          value={ORDER_TAB.COMPLETED_ORDER}
          className={`flex flex-col items-center border rounded-xl hover:scale-105 transition-all duration-300
            ${selectedTab !== ORDER_TAB.COMPLETED_ORDER ? 'borde-hzc_beige_400' : 'border-hzc_purple_500'}`}
          onClick={() => setSelectedTab(ORDER_TAB.COMPLETED_ORDER)}
        >
          <div className="flex flex-row gap-2 justify-start items-center w-full">
            <div className="flex items-center justify-center size-9 rounded-full bg-green-200">
              <Package className="text-green-500 w-4 h-4" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-base font-medium text-hzc_beige_900">
                {ordersList[ORDER_TAB.COMPLETED_ORDER]?.count}
              </span>
              <span className="text-xs text-hzc_beige_700">
                {ordersList[ORDER_TAB.COMPLETED_ORDER]?.title}
              </span>
            </div>
          </div>
        </TabsTrigger>
        <TabsTrigger
          value={ORDER_TAB.CANCELED}
          className={`flex flex-col items-center border rounded-xl hover:scale-105 transition-all duration-300
            ${selectedTab !== ORDER_TAB.CANCELED ? 'borde-hzc_beige_400' : 'border-hzc_purple_500'}`}
          onClick={() => setSelectedTab(ORDER_TAB.CANCELED)}
        >
          <div className="flex flex-row gap-2 justify-start items-center w-full">
            <div className="flex items-center justify-center size-9 rounded-full bg-red-200">
              <Package className="text-red-500 w-4 h-4" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-base font-medium text-hzc_beige_900">
                {ordersList[ORDER_TAB.CANCELED]?.count}
              </span>
              <span className="text-xs text-hzc_beige_700">
                {ordersList[ORDER_TAB.CANCELED]?.title}
              </span>
            </div>
          </div>
        </TabsTrigger>
      </TabsList>
      <TabsContent value={ORDER_TAB.TOTAL_ORDER} className="flex flex-col gap-4 mt-4">
        {ordersList[ORDER_TAB.TOTAL_ORDER]?.orders?.map((order) => (
          <OrderHistoryView
            key={order.orderId}
            order={order}
            onClick={() => openOrderDetail(order)}
          />
        ))}
      </TabsContent>
      <TabsContent value={ORDER_TAB.ACTIVE_ORDER} className="flex flex-col gap-4 mt-4">
        {ordersList[ORDER_TAB.ACTIVE_ORDER]?.orders?.map((order) => (
          <OrderHistoryView
            key={order.orderId}
            order={order}
            onClick={() => openOrderDetail(order)}
          />
        ))}
      </TabsContent>
      <TabsContent value={ORDER_TAB.COMPLETED_ORDER} className="flex flex-col gap-4 mt-4">
        {ordersList[ORDER_TAB.COMPLETED_ORDER]?.orders?.map((order) => (
          <OrderHistoryView
            key={order.orderId}
            order={order}
            onClick={() => openOrderDetail(order)}
          />
        ))}
      </TabsContent>
      <TabsContent value={ORDER_TAB.CANCELED} className="flex flex-col gap-4 mt-4">
        {ordersList[ORDER_TAB.CANCELED]?.orders?.map((order) => (
          <OrderHistoryView
            key={order.orderId}
            order={order}
            onClick={() => openOrderDetail(order)}
          />
        ))}
      </TabsContent>
    </Tabs>
  );
}

function OrderHistoryView({ order, onClick }: { order: Order; onClick: Function }) {
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [ratedItems, setRatedItems] = useState<number[]>([]);
  const hasUnratedItems = order.items.some(
    (item) => !item.hasRated && !item.isExtra && !ratedItems.includes(item.id),
  );

  return (
    <div className="w-full rounded-xl border border-hzc_beige_400 bg-white p-4 flex flex-col gap-3">
      <div className="w-full flex flex-row justify-between items-start gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-hzc_purple_500">
            Sipariş #{order.orderNumber}
          </span>
          <span className="text-xs text-hzc_beige_700">
            {formatOrderDate(order.orderStartTime)}
          </span>
          <OrderStatus status={order.orderStatus} />
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className="text-sm text-hzc_purple_500 font-bold">
            {formatNumberToPriceText(order.total)} TL
          </span>
          <Button
            type="button"
            className="h-8 rounded-full bg-hzc_purple_500 px-3 text-xs text-white hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300"
            onClick={() => onClick()}
          >
            Detay
            <ChevronRight className="ml-1 size-4" />
          </Button>
        </div>
      </div>

      <div className="w-full flex flex-col gap-3">
        {order.items.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="flex flex-row gap-3 items-center border-t border-hzc_beige_300 pt-3 first:border-t-0 first:pt-0"
          >
            {item.imageUrl && (
              <figure className="shrink-0">
                <div className="overflow-hidden rounded-md">
                  <SmartImage
                    src={item.imageUrl}
                    alt={`${item.title} - Ürün Görseli`}
                    className="aspect-[1] object-cover"
                    width={72}
                    height={72}
                    imageType="icon"
                  />
                </div>
              </figure>
            )}
            <div className="min-w-0 flex-1 flex flex-col gap-1">
              <span className="text-sm text-hzc_beige_900 font-medium line-clamp-2">
                {item.title}
              </span>
              <div className="flex flex-row flex-wrap gap-x-4 gap-y-1">
                <span className="text-xs text-hzc_beige_700">
                  Adet: <span className="text-hzc_beige_900 font-medium">{item.count}</span>
                </span>
                <span className="text-xs text-hzc_beige_700">
                  Fiyat:{' '}
                  <span className="text-hzc_beige_900 font-medium">
                    {formatNumberToPriceText(item.price)} TL
                  </span>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {order.orderStatus === ORDER_STATUS.SHIPPED && order.shipmentId && (
        <a
          href={`https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code=${order.shipmentId}`}
          className="text-xs text-hzc_purple_500 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Kargo takip için tıklayın.
        </a>
      )}

      {order.orderStatus === ORDER_STATUS.DELIVERED && hasUnratedItems && (
        <Button
          type="button"
          className="w-full text-hzc_purple_500 bg-hzc_purple_500/10 hover:bg-hzc_purple_500/10 hover:scale-[1.01] transition-all duration-300 gap-2"
          onClick={() => setIsReviewOpen(true)}
        >
          <PenLine className="size-4" />
          Ürünleri değerlendir
        </Button>
      )}

      <OrderReviewDialog
        order={order}
        isOpen={isReviewOpen}
        setIsOpen={setIsReviewOpen}
        ratedItems={ratedItems}
        setRatedItems={setRatedItems}
      />
    </div>
  );
}

export function OrderStatus({ status }: { status: number }) {
  let text: string | undefined;
  let icon: JSX.Element | undefined;

  switch (status) {
    case ORDER_STATUS.PAYMENT_PENDING:
      text = 'Ödeme Bekleniyor';
      icon = <Hourglass className="text-orange-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.PAYMENT_DONE:
      text = 'Ödeme Alındı';
      icon = <HandCoins className="text-green-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.PREPARING:
    case ORDER_STATUS.PRINTED:
    case ORDER_STATUS.ON_THE_COUNTER:
      text = 'Hazırlanıyor';
      icon = <PackageOpen className="text-blue-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.ASSIGNED_TO_COURIER:
      text = 'Teslim Edilecek';
      icon = <Package className="text-blue-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.SHIPPED:
      text = 'Kargoda';
      icon = <Package className="text-blue-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.ON_DELIVERY:
      text = 'Dağıtımda';
      icon = <Truck className="text-blue-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.DELIVERED:
      text = 'Teslim Edildi';
      icon = <PackageCheck className="text-green-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.CUSTOMER_CANCELLED:
      text = 'İptal Edildi';
      icon = <MessageCircleX className="text-red-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.SYSTEM_CANCELLED:
    case ORDER_STATUS.CUSTOMER_OVERRIDE:
      text = 'İptal Edildi';
      icon = <CircleX className="text-red-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.CUSTOMER_RETURNED:
    case ORDER_STATUS.WAREHOUSE_CANCELLED:
      text = 'İptal Edildi';
      icon = <CircleArrowLeft className="text-red-500 w-4 h-4" />;
      break;
    case ORDER_STATUS.ADMIN_CANCELLED:
      text = 'İptal Edildi';
      icon = <SquareX className="text-red-500 w-4 h-4" />;
      break;
    default:
      text = undefined;
      icon = <MessageCircleWarning className="text-gray-500 w-4 h-4" />;
  }

  return (
    <div className="flex flex-row gap-1 items-center">
      {icon}
      <span className="text-xs font-medium text-hzc_beige_900">{text}</span>
    </div>
  );
}

function OrderReviewDialog({
  order,
  isOpen,
  setIsOpen,
  ratedItems,
  setRatedItems,
}: {
  order: Order;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  ratedItems: number[];
  setRatedItems: Dispatch<SetStateAction<number[]>>;
}) {
  const { toast } = useToast();
  const [review, setReview] = useState<{
    [key: string]: {
      review: string;
      rating: number;
    };
  }>({});

  useEffect(() => {
    const unratedItems = order?.items.filter(
      (item) => !item.isExtra && !item.hasRated && !ratedItems.includes(item.id),
    );

    if (unratedItems?.length === 0) {
      setIsOpen(false);
    }
  }, [order, ratedItems, setIsOpen]);

  async function sendReview(productId: number) {
    const reviewData = {
      review: review[productId.toString()]?.review,
      rating: review[productId.toString()]?.rating || 5,
    };

    // if (!reviewData || !reviewData.review || reviewData.review?.length < 10) {
    //   toast({
    //     variant: 'destructive',
    //     description: 'Lütfen açıklayıcı bir değerlendirme giriniz.',
    //   });
    //   return;
    // }

    if (!reviewData || !reviewData.rating) {
      toast({
        variant: 'destructive',
        description: 'Lütfen geçerli bir puan veriniz.',
      });
      return;
    }

    try {
      const body = {
        productId,
        review: reviewData.review,
        orderId: order.orderId,
        rating: reviewData.rating,
      };
      const { data: result } = await Requester.post<GenericResponse<StatusResponse>>(
        Endpoints.ADD_REVIEW,
        body,
      );
      if (result.data.status) {
        toast({
          variant: 'default',
          description: 'Değerlendirmeniz başarıyla kaydedildi',
        });
        setRatedItems((prev) => [...prev, productId]);
      } else {
        toast({
          variant: 'destructive',
          description: 'Bir hata oluştu',
        });
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[32px] text-hzc_purple_500">
            Ürünleri değerlendir
          </DialogTitle>
        </DialogHeader>
        <div className="w-full flex flex-col gap-4">
          {order?.items
            .filter((item) => !item.isExtra && !item.hasRated && !ratedItems.includes(item.id))
            .map((item, index) => (
              <div key={item.id} className={`${index > 0 && 'border-t'} pt-2 flex flex-col gap-2`}>
                <div className="flex flex-row justify-between items-center">
                  <Link
                    href={item.url || ''}
                    className="flex items-center gap-2 hover:scale-105 transition-all duration-300"
                  >
                    <SmartImage
                      src={item.imageUrl}
                      alt={`${item.title} - Ürün Görseli`} //alty
                      className="aspect-[1] object-cover rounded-md"
                      width={48}
                      height={48}
                      imageType="icon"
                    />
                    <span className="text-sm">{item.title}</span>
                  </Link>
                  <Button
                    className="rounded-full bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300"
                    onClick={() => sendReview(item.id)}
                  >
                    Gönder
                  </Button>
                </div>
                <Ratings
                  defaultValue={5}
                  rating={review[item.id.toString()]?.rating || 5}
                  onRatingChange={(value) =>
                    setReview((prev) => ({
                      ...prev,
                      [item.id.toString()]: item?.id?.toString()
                        ? {
                            ...prev[item.id.toString()],
                            rating: value,
                          }
                        : {
                            review: '',
                            rating: value,
                          },
                    }))
                  }
                  variant="yellow"
                />
                <Textarea
                  placeholder="Ürün hakkındaki düşüncelerinizi yazınız"
                  className="max-h-[128px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm mt-2"
                  value={review[item.id.toString()]?.review}
                  onChange={(e) =>
                    setReview((prev) => ({
                      ...prev,
                      [item.id.toString()]: item?.id?.toString()
                        ? {
                            ...prev[item.id.toString()],
                            review: e.target.value,
                          }
                        : {
                            review: e.target.value,
                            rating: 5,
                          },
                    }))
                  }
                />
              </div>
            ))}
        </div>
        <DialogFooter className="gap-2">
          <Button
            className="text-hzc_purple_500 bg-[#5E6AFF1A] hover:bg-[#5E6AFF1A] transition-all duration-300 hover:scale-105 rounded-full h-[48px]"
            onClick={() => setIsOpen(false)}
          >
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
